import React, { useCallback, useMemo, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { normalizeRole, permisosSobreUsuarioAdministrativo } from "../../utils/roles.utils";
import {
  Button,
  Table,
  Input,
  Modal,
  Form,
  Popconfirm,
  Tabs,
  Tag,
  Space,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Layout from "../../Components/Layout";
import PasswordModal from "../../Components/PasswordModal";
import PageHeader from "../../Components/PageHeader";
import TelefonoInput from "../../Components/TelefonoInput";
import AvisoTelefonosRepetidos from "../../Components/AvisoTelefonosRepetidos";
import { useAdministradoresLogic } from "./useAdministradoresLogic";
import { formatPhoneConCodigo } from "../../utils/formatPhone";
import { reglaTelefono } from "../../utils/telefonoE164";
import { MIN_PASSWORD_LENGTH, passwordMinRule } from "../../utils/passwordPolicy";
import ConfigTab from "./ConfigTab";
import GeoTab from "./GeoTab";
import ImageOptimizationTab from "./ImageOptimizationTab";
import VideoOptimizationTab from "./VideoOptimizationTab";
import PreciosGuardTab from "./PreciosGuardTab";
import AseosVentanaTab from "./AseosVentanaTab";
import NotificacionesTab from "./NotificacionesTab";
import AsistenteWhatsappTab from "./AsistenteWhatsappTab";
import DbQueryTab from "./DbQueryTab";
import OrphanCleaningsTab from "./OrphanCleaningsTab";
import StaysEventsMaintenanceTab from "./StaysEventsMaintenanceTab";
import StaysBlocksTab from "./StaysBlocksTab";
import type { AdminUser } from "../../interfaces/api.interfaces";
import { useEsMovil } from "../aseos/components/useMediaQuery";
import "../../Css/tabla-fichas-movil.css";

/**
 * Barra del buscador y el botón «Nuevo Administrador» (ronda F3b). Antes el buscador era `flex: 1` con tope de 400 px: junto al botón
 * (207 px) en 360 px sobraban 45 px y apenas cabía el icono. Ahora parte de 260 px: en un celular no cabe al lado del botón, así que
 * ocupa su propia fila entera y el botón queda debajo; en una pantalla ancha sigue a su lado. Se exporta para que la prueba lo mida.
 */
export const ESTILO_BUSCADOR_ADMINISTRADORES: CSSProperties = { flex: "1 1 260px", minWidth: 0, maxWidth: 400 };

/**
 * El rol de la fila como etiqueta (tabla de escritorio y ficha del celular).
 * Post-refactor: `rol` es string escalar. Mantenemos guard defensivo para filas legacy que todavía lleguen como array (la DB ya fue
 * normalizada por schema.bootstrap, pero no queremos bloquear la pestaña si un dato viejo pasara).
 * Bug previo: `(roles || []).map` crashea con un string (porque un string es truthy pero no tiene .map).
 */
const etiquetaDeRol = (rol: unknown) => {
  const normalized = Array.isArray(rol) ? String((rol as unknown[])[0] ?? "") : String(rol ?? "");
  if (!normalized) return null;
  return <Tag color={normalized === "superadministrador" ? "purple" : "blue"}>{normalized}</Tag>;
};

// Round 2026-04-30 septies — la pestaña "Claves API" se eliminó por
// completo. En producción las variables se configuran en Railway →
// Variables; el panel solo era visor + rotación para dev local, lo cual
// duplicaba la fuente de verdad y confundía al admin (rotaciones que no
// persisten porque Railway re-inyecta las vars en cada deploy). Si en
// el futuro se quiere visor read-only, abrir issue.

const AdministradoresPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // `user.rol` string escalar (ver CLAUDE.md §3.3 y WARNING en roles.utils.ts).
  const isSuperAdmin = normalizeRole(user?.rol) === "superadministrador";
  // Celular: la tabla de ~800 px deja Editar y Eliminar fuera de la pantalla; ahí cada fila pasa a una ficha de una columna (ronda F3b).
  const esMovil = useEsMovil();

  // Deep-link por query param `?tab=`: permite aterrizar directo en una
  // pestaña concreta (lo usa el atajo "Administrar bancos y tipos de cuenta"
  // del formulario de clientes → `?tab=configuracion`). Si el param no es una
  // pestaña conocida, caemos al default por rol.
  const KNOWN_TABS = [
    "administradores",
    "configuracion",
    "zonas",
    "imagenes",
    "videos",
    "umbral-precios",
    "notificaciones",
    "asistente-whatsapp",
    "db-query",
    "orphan-cleanings",
    "stays-events-maintenance",
    "stays-blocks-coexisting",
  ];
  // Pestañas reservadas al superadministrador. Sirven para no restaurarle a un
  // admin común una pestaña que su rol ya no puede ver (quedaría en blanco).
  const TABS_SUPERADMIN = [
    "administradores",
    "db-query",
    "orphan-cleanings",
    "stays-events-maintenance",
    "stays-blocks-coexisting",
  ];
  const tabParam = searchParams.get("tab");
  const pestanasValidas = KNOWN_TABS.filter(
    (t) => isSuperAdmin || !TABS_SUPERADMIN.includes(t),
  );
  // El enlace directo también tiene que respetar el rol: una pestaña de
  // superadmin abierta por un admin común apuntaría a una key que no existe
  // en `items` y dejaría la pantalla en blanco. Si no es válida para su rol,
  // se ignora el parámetro y cae al default (o a lo que tenga guardado).
  const hayEnlaceDirecto = Boolean(
    tabParam && pestanasValidas.includes(tabParam),
  );
  const defaultTab = hayEnlaceDirecto
    ? (tabParam as string)
    : isSuperAdmin
      ? "administradores"
      : "configuracion";

  const {
    activeTab,
    setActiveTab,
    admins,
    gruposTelefonoRepetido,
    saving,
    loading,
    modalVisible,
    editingRecord,
    generatedPassword,
    setGeneratedPassword,
    form,
    sidebarOpen,
    toggleSidebar,
    searchInput,
    setSearchInput,
    showModal,
    handleCancel,
    handleSubmit,
    handleDelete,
  } = useAdministradoresLogic(defaultTab as any, {
    // La pestaña abierta se recuerda entre visitas (round 2026-09-24), salvo
    // que se llegue con `?tab=`: ese atajo manda sobre lo guardado.
    pestanasValidas,
    restaurar: !hayEnlaceDirecto,
  });

  // Los botones de una fila. Los comparten la tabla de escritorio y la ficha del celular: lo que se ofrece no puede depender del ancho.
  const accionesDe = useCallback(
    (record: AdminUser) => {
      // Qué botones se ofrecen lo deciden el rol de QUIEN MIRA y el de la FILA (docs/auth-roles.md §4); no se espera a que el
      // servidor responda 403. Antes la fila de un superadministrador mostraba «Editar» y guardar siempre fallaba.
      // `esLaPropiaCuenta`: la fila de quien mira NO se edita acá aunque sea superadministrador. Cambiar el correo propio exige la
      // contraseña actual (decisión del dueño, S-03) y ese flujo vive en Mi perfil; a OTRO superadministrador sí se le corrige acá.
      const permisos = permisosSobreUsuarioAdministrativo(user?.rol, record.rol, {
        esLaPropiaCuenta: Number(record.id) === Number(user?.id),
      });
      return (
        <Space size={4}>
          {permisos.puedeEditar ? (
            <Button
              icon={<EditOutlined />}
              aria-label={`Editar a ${record.nombre_usuario}`}
              onClick={() => showModal(record)}
            />
          ) : (
            permisos.motivoNoEditar && (
              // El motivo se ve SIEMPRE, escrito bajo el botón: un Tooltip solo existe para quien pasa el ratón, y con teclado, con
              // lector de pantalla o con el dedo en el celular nadie lo veía (quedaba un lápiz gris sin explicación).
              <div style={{ maxWidth: 240 }}>
                <Button
                  icon={<EditOutlined />}
                  aria-label={`Editar a ${record.nombre_usuario}`}
                  aria-describedby={`motivo-no-editar-${record.id}`}
                  disabled
                />
                <div id={`motivo-no-editar-${record.id}`} style={{ marginTop: 4, fontSize: 12, lineHeight: 1.4, color: "#475569" }}>
                  {permisos.motivoNoEditar}
                </div>
              </div>
            )
          )}
          {permisos.puedeEliminar && (
            <Popconfirm
              title="¿Eliminar este administrador?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button danger icon={<DeleteOutlined />} aria-label={`Eliminar a ${record.nombre_usuario}`} />
            </Popconfirm>
          )}
        </Space>
      );
    },
    [showModal, handleDelete, user?.rol, user?.id],
  );

  // Celular (ronda F3b): UNA columna por fila, la ficha. Nombre y rol arriba, correo y teléfono, y los botones a la vista SIN desplazar
  // (en 360 px, Editar quedaba a 754 px del borde izquierdo). Mismos botones que la tabla de escritorio.
  const columnsMovil = useMemo(
    () => [
      {
        key: "ficha",
        render: (_: unknown, record: AdminUser) => (
          <div className="ficha">
            <div className="ficha-titulo">
              <span>{record.nombre_usuario}</span>
              {etiquetaDeRol(record.rol)}
            </div>
            <div className="ficha-dato">{record.email_usuario}</div>
            {record.telefono_usuario ? (
              <div className="ficha-dato">
                <span className="ficha-dato-etiqueta">Teléfono</span>
                <span>{formatPhoneConCodigo(record.telefono_usuario)}</span>
              </div>
            ) : null}
            <div className="ficha-acciones">{accionesDe(record)}</div>
          </div>
        ),
      },
    ],
    [accionesDe],
  );

  const columnsAdmin = useMemo(
    () => [
      {
        title: "Nombre",
        dataIndex: "nombre_usuario",
        key: "nombre_usuario",
      },
      {
        title: "Email",
        dataIndex: "email_usuario",
        key: "email_usuario",
      },
      {
        title: "Teléfono",
        dataIndex: "telefono_usuario",
        key: "telefono_usuario",
        // El teléfono del equipo lleva código de país: se muestra con el código separado (formatPhoneDisplay partía el +54, +34, +1…).
        render: (v: string) => formatPhoneConCodigo(v),
      },
      {
        title: "Rol",
        dataIndex: "rol",
        key: "rol",
        render: etiquetaDeRol,
      },
      {
        title: "Acciones",
        key: "acciones",
        render: (_: any, record: AdminUser) => accionesDe(record),
      },
    ],
    [accionesDe],
  );

  // Editar a OTRO superadministrador (round 2026-10-31): nombre, correo y teléfono; el rol nunca se cambia desde esta pantalla.
  const editandoSuperadmin = !!editingRecord && normalizeRole(editingRecord.rol) === "superadministrador";
  const modalTitle = editingRecord
    ? editandoSuperadmin
      ? "Editar superadministrador"
      : "Editar administrador"
    : "Nuevo administrador";

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <section className="content-section">
        <PageHeader
          title="Configuración"
          helpText="Configuración del sistema, opciones y zonas geográficas."
        />

        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as any)}
          items={[
            ...(isSuperAdmin
              ? [
                  {
                    key: "administradores",
                    label: "Administradores",
                    children: (
                      <>
                        {/* Dos o más del equipo con el mismo número: el asistente de WhatsApp no sabría quién es quién. */}
                        <AvisoTelefonosRepetidos grupos={gruposTelefonoRepetido} />
                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            marginBottom: 16,
                            flexWrap: "wrap",
                          }}
                        >
                          <Input
                            placeholder="Buscar por nombre o correo"
                            allowClear
                            size="large"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={ESTILO_BUSCADOR_ADMINISTRADORES}
                          />
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={() => showModal()}
                            style={{ flex: "0 0 auto" }}
                          >
                            Nuevo Administrador
                          </Button>
                        </div>
                        {/* Celular: una ficha por fila, con Editar y Eliminar a la vista (la tabla de 800 px los dejaba fuera de la pantalla). */}
                        <Table
                          className={esMovil ? "tabla-fichas" : undefined}
                          columns={esMovil ? columnsMovil : columnsAdmin}
                          showHeader={!esMovil}
                          dataSource={admins}
                          rowKey="id"
                          loading={loading}
                          scroll={esMovil ? undefined : { x: "max-content" }}
                        />
                      </>
                    ),
                  },
                ]
              : []),
            {
              key: "configuracion",
              label: "Opciones generales",
              children: <ConfigTab />,
            },
            // Round 2026-10-30 (etapa B3 del bot) — panel del asistente de
            // WhatsApp: modo real (apagado por defecto; solo el superadmin lo
            // enciende), modelos y límites, modo de pruebas, simulaciones y
            // actividad reciente. Visible para admin y superadmin; los
            // interruptores delicados los gobierna `esSuperadmin` (y el servidor).
            // Va justo después de «Opciones generales» (y no al final): con tantas
            // pestañas repartidas en filas, en el celular la última queda muy abajo.
            // `activa` apaga la lectura periódica cuando se está viendo otra pestaña.
            {
              key: "asistente-whatsapp",
              label: "Asistente de WhatsApp",
              children: <AsistenteWhatsappTab esSuperadmin={isSuperAdmin} activa={(activeTab as string) === "asistente-whatsapp"} />,
            },
            {
              key: "zonas",
              label: "Zonas geográficas",
              children: <GeoTab />,
            },
            {
              key: "imagenes",
              label: "Optimización de imágenes",
              children: <ImageOptimizationTab />,
            },
            {
              key: "videos",
              label: "Optimización de videos",
              children: <VideoOptimizationTab />,
            },
            // Round 2026-06-30 — umbral global de precio bajo (red anti-error
            // de tipeo al fijar el precio por noche base en el calendario).
            // Round 2026-07-03 — se agregó el IVA (%) configurable del sistema
            // (misma pestaña, mismo singleton general-settings). El `key` se
            // conserva por compat de deep-links. Ver docs/flujo-financiero.md §8.
            {
              key: "umbral-precios",
              label: "Precios e IVA",
              children: <PreciosGuardTab />,
            },
            // Round 2026-10-28 — ventana de aseos abiertos: cuántos días hacia
            // adelante ve y puede tomar una aseadora un aseo sin asignar (antes
            // constante de 14). Mismo singleton general-settings. Ver
            // docs/rol-aseador.md §5.8.
            {
              key: "aseos-abiertos",
              label: "Aseos abiertos",
              children: <AseosVentanaTab />,
            },
            // Round 2026-07-28 — interruptor de los correos de aviso al
            // cliente (cierre de mes y observación compartida). NO toca los
            // transaccionales (recuperación de contraseña, bienvenida). La
            // capa por propiedad vive en la ficha de cada una.
            // Ver docs/integraciones-externas.md §5.
            {
              key: "notificaciones",
              label: "Correos al cliente",
              children: <NotificacionesTab />,
            },
            // Round 2026-05-28 — diagnóstico SQL read-only, solo
            // superadmin. Backend gateado igual. Sirve para debugar
            // sin tener que entrar a Railway psql (ej. ver por qué
            // un webhook de Stays quedó atascado en stays_events).
            ...(isSuperAdmin
              ? [
                  {
                    key: "db-query",
                    label: "Diagnóstico SQL",
                    children: <DbQueryTab />,
                  },
                  // Round 2026-05-29 — limpieza de aseos huérfanos
                  // (reserva_id apuntando a reserva borrada). Solo
                  // superadmin: aplica un UPDATE que desvincula los
                  // aseos. Ver docs/calendario-cascada-arriendos.md.
                  {
                    key: "orphan-cleanings",
                    label: "Aseos huérfanos",
                    children: <OrphanCleaningsTab />,
                  },
                  // Round 2026-05-29 fase 4 — mantenimiento del cron de
                  // Stays. Permite buscar/re-encolar eventos para que el
                  // cron los re-procese (failed por bug fixeado, done sin
                  // efecto real, queued atascado). Caso del bug de
                  // VISTAMAR/Carolina documentado en modulo-stays.md §6.3.
                  {
                    key: "stays-events-maintenance",
                    label: "Mantenimiento Stays",
                    children: <StaysEventsMaintenanceTab />,
                  },
                  // Round 2026-05-30 — detección y borrado de bloqueos
                  // de Stays (id_reserval != NULL) que quedaron en DB
                  // coexistiendo con su reserva real (tentativos que el
                  // delete nunca limpió). Patrón documentado en
                  // docs/modulo-stays.md §3.1.F.
                  {
                    key: "stays-blocks-coexisting",
                    label: "Bloqueos Stays",
                    children: <StaysBlocksTab />,
                  },
                ]
              : []),
          ]}
        />

        {/* Modal crear/editar administrador */}
        <Modal
          title={modalTitle}
          open={modalVisible}
          onOk={handleSubmit}
          onCancel={handleCancel}
          okText={editingRecord ? "Actualizar" : "Crear"}
          confirmLoading={saving}
          width={500}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre_usuario"
              label="Nombre"
              rules={[{ required: true, message: "El nombre es obligatorio" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email_usuario"
              label="Email"
              // Al corregir a un superadministrador el servidor avisa a su correo anterior: se le dice a quien edita.
              extra={editandoSuperadmin ? "Si cambias el correo, se avisará al correo anterior de este superadministrador." : undefined}
              rules={[
                { required: true, message: "El email es obligatorio" },
                { type: "email", message: "Ingrese un email válido" },
              ]}
            >
              <Input placeholder="correo@ejemplo.com" />
            </Form.Item>
            {/* Teléfono con código de país obligatorio (el servidor exige la misma forma). Un dato antiguo sin código que no se toca
                no impide guardar el resto; apenas se cambia tiene que estar completo. */}
            <Form.Item
              name="telefono_usuario"
              label="Teléfono"
              rules={[reglaTelefono({ valorInicial: editingRecord?.telefono_usuario })]}
            >
              <TelefonoInput
                ariaLabel="Teléfono del administrador"
                avisoSiVacio={
                  editingRecord?.telefono_usuario
                    ? "Si dejas el teléfono vacío, el asistente de WhatsApp no podrá reconocer a este administrador."
                    : undefined
                }
              />
            </Form.Item>

            {/*
              Política unificada de contraseñas (Opción B — ver
              docs/auth-roles.md §"Creación de cuentas").

              Solo aplica al crear (no al editar — el cambio de
              contraseña de un admin se hace por "recuperar".)

                - Vacío → se autogenera una contraseña temporal y se
                  envía al correo del nuevo administrador. Verás un
                  modal con la contraseña al final para copiarla.
                - Con valor → será la contraseña inicial y también
                  se enviará por correo de bienvenida.
            */}
            {/* El rol se muestra pero no se cambia desde acá (el formulario ni siquiera lo envía): promover o degradar a alguien tiene
                su propia puerta. Sin `name`, no forma parte de los valores del formulario. */}
            {editingRecord && (
              <Form.Item label="Rol" extra="El rol no se puede cambiar desde esta pantalla.">
                <Input
                  aria-label="Rol"
                  disabled
                  value={editandoSuperadmin ? "Superadministrador" : "Administrador"}
                />
              </Form.Item>
            )}

            {!editingRecord && (
              <Form.Item
                name="password"
                label="Contraseña inicial"
                extra={`Opcional. Si la dejas vacía, se generará automáticamente y se enviará por correo. Si la escribes (mínimo ${MIN_PASSWORD_LENGTH} caracteres), será la contraseña inicial del administrador y también se enviará por correo.`}
                rules={[passwordMinRule]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            )}
          </Form>
        </Modal>

        {/* Modal contraseña generada */}
        <PasswordModal
          open={!!generatedPassword}
          password={generatedPassword}
          title="Contraseña generada para el administrador"
          onClose={() => setGeneratedPassword(null)}
        />
      </section>
    </Layout>
  );
};

export default AdministradoresPage;
